(() => {
  const selectors = {
    drawer: "[data-cart-drawer]",
    cartPage: "[data-cart-page]",
    backdrop: "[data-cart-backdrop]",
    openers: "[data-cart-open]",
    closer: "[data-cart-close]",
    closeLink: "[data-cart-close-link]",
    qtyMinus: "[data-qty-minus]",
    qtyPlus: "[data-qty-plus]",
    qtyInput: "[data-qty-input]",
    remove: "[data-cart-remove]",
    addToCartForms: "form[action^='/cart/add']",
    cartSellingPlanSelect: "[data-cart-selling-plan-select]",
    cartCount: "[data-cart-count]",
    cartSubtotal: "[data-cart-subtotal]",
  };

  const state = {
    isOpen: false,
    isLoading: false,
    returnFocusTo: null,
  };

  const focusableSelector =
    "a[href], button:not([disabled]), input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

  function getFocusable(root) {
    return qsa(focusableSelector, root).filter((el) => el.getClientRects().length > 0);
  }

  function qs(sel, root = document) {
    return root.querySelector(sel);
  }

  function qsa(sel, root = document) {
    return Array.from(root.querySelectorAll(sel));
  }

  function lockScroll(lock) {
    document.documentElement.style.overflow = lock ? "hidden" : "";
  }

  function getMoneyFormat() {
    return window.theme?.moneyFormat || window.Shopify?.money_format || "${{amount}}";
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents || 0, getMoneyFormat());
    }
    return `$${((Number(cents || 0)) / 100).toFixed(2)}`;
  }

  function openDrawer() {
    const drawer = qs(selectors.drawer);
    const backdrop = qs(selectors.backdrop);
    if (!drawer || !backdrop) return;

    drawer.setAttribute("aria-hidden", "false");
    drawer.classList.add("is-open");
    backdrop.hidden = false;
    backdrop.classList.add("is-open");

    // Move keyboard/screen-reader focus into the drawer, remembering where to return it.
    if (!state.isOpen) {
      const active = document.activeElement;
      state.returnFocusTo = active && !drawer.contains(active) ? active : null;
    }
    drawer.focus({ preventScroll: true });

    state.isOpen = true;
    lockScroll(true);
  }

  function closeDrawer() {
    const drawer = qs(selectors.drawer);
    const backdrop = qs(selectors.backdrop);
    if (!drawer || !backdrop) return;

    drawer.setAttribute("aria-hidden", "true");
    drawer.classList.remove("is-open");
    backdrop.classList.remove("is-open");

    state.isOpen = false;
    lockScroll(false);

    const returnTo = state.returnFocusTo;
    state.returnFocusTo = null;
    if (returnTo && returnTo.isConnected && drawer.contains(document.activeElement)) {
      returnTo.focus({ preventScroll: true });
    }

    setTimeout(() => {
      if (!state.isOpen) backdrop.hidden = true;

      if (isCartPageRoute() && cartPageNeedsRefresh()) {
        clearCartPageRefreshFlag();
        window.location.reload();
      }
    }, 150);
  }


  async function fetchSectionHTML() {
    const url = `${window.location.pathname}?section_id=cart-drawer`;
    const res = await fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } });
    if (!res.ok) throw new Error("Failed to fetch cart drawer section");
    return await res.text();
  }

  function replaceDrawerFromHTML(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const newDrawer = doc.querySelector(selectors.drawer);
    const newBackdrop = doc.querySelector(selectors.backdrop);

    const curDrawer = qs(selectors.drawer);
    const curBackdrop = qs(selectors.backdrop);

    const shouldBeOpen = state.isOpen;
    const hadFocus = !!curDrawer && curDrawer.contains(document.activeElement);

    if (newDrawer && curDrawer) {
      curDrawer.replaceWith(newDrawer);
      if (shouldBeOpen) {
        newDrawer.classList.add("is-open");
        newDrawer.setAttribute("aria-hidden", "false");
        if (hadFocus) newDrawer.focus({ preventScroll: true });
      }
    }

    if (newBackdrop && curBackdrop) {
      curBackdrop.replaceWith(newBackdrop);
      if (shouldBeOpen) {
        newBackdrop.hidden = false;
        newBackdrop.classList.add("is-open");
      } else {
        newBackdrop.classList.remove("is-open");
        newBackdrop.hidden = true;
      }
    }
  }

  async function refreshDrawer({ open = true } = {}) {
    if (state.isLoading) return;
    state.isLoading = true;

    try {
      const html = await fetchSectionHTML();
      replaceDrawerFromHTML(html);
      bindDrawerEvents();
      if (open) openDrawer();
      await updateHeaderCount();
    } catch (e) {
      console.error(e);
    } finally {
      state.isLoading = false;
    }
  }

  async function getCart() {
    const res = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error("Failed to fetch cart");
    return await res.json();
  }

  function updateHeaderCountFromCart(cart) {
    const el = qs(selectors.cartCount);
    if (!el) return;

    const count = cart?.item_count ?? 0;
    const hiddenText = el.querySelector(".visually-hidden");

    el.textContent = String(count);

    if (hiddenText) {
      el.appendChild(hiddenText);
    } else {
      const span = document.createElement("span");
      span.className = "visually-hidden";
      span.textContent = "items in cart";
      el.appendChild(span);
    }

    el.toggleAttribute("hidden", count <= 0);
  }

  function updateAllSubtotals(cart) {
    qsa(selectors.cartSubtotal).forEach((el) => {
      el.textContent = formatMoney(cart?.items_subtotal_price || 0);
    });
  }

  async function updateHeaderCount() {
    const el = qs(selectors.cartCount);
    if (!el) return;

    try {
      const cart = await getCart();
      updateHeaderCountFromCart(cart);
    } catch (_) {}
  }

  async function cartChange(key, quantity, context = "drawer") {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity }),
    });

    if (!res.ok) {
      throw new Error("Failed to change cart quantity");
    }

    const cart = await res.json();
    updateHeaderCountFromCart(cart);
    updateAllSubtotals(cart);

    if (context === "page") {
      window.location.reload();
      return;
    }

    openDrawer();
    await refreshDrawer({ open: false });

    if (isCartPageRoute()) {
      markCartPageNeedsRefresh();
    }
  }



  async function cartAdd(form) {
    const formData = new FormData(form);

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: formData,
    });

    if (!res.ok) {
      let errorText = "Failed to add item to cart";
      try {
        const errorJson = await res.json();
        errorText = errorJson?.description || errorJson?.message || errorText;
      } catch (_) {}
      throw new Error(errorText);
    }

    if (qs(selectors.drawer)) {
      openDrawer();
      await refreshDrawer({ open: false });
    } else {
      window.location.href = "/cart";
    }
  }

  async function removeLineByIndex(lineIndex) {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        line: Number(lineIndex),
        quantity: 0,
      }),
    });

    if (!res.ok) {
      throw new Error("Failed to remove existing cart line");
    }

    return await res.json();
  }

  async function addLineWithPlan({ variantId, quantity, sellingPlanId }) {
    const payload = {
      id: Number(variantId),
      quantity: Number(quantity || 1),
    };

    if (sellingPlanId) {
      payload.selling_plan = Number(sellingPlanId);
    }

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error("Failed to add cart line with updated selling plan");
    }

    return await res.json();
  }

  async function changeLineSellingPlan({ lineIndex, variantId, quantity, sellingPlanId, context = "drawer" }) {
    await removeLineByIndex(lineIndex);

    await addLineWithPlan({
      variantId,
      quantity,
      sellingPlanId,
    });

    const cart = await getCart();
    updateHeaderCountFromCart(cart);
    updateAllSubtotals(cart);

    if (context === "page") {
      window.location.reload();
      return;
    }

    await refreshDrawer({ open: false });

    if (isCartPageRoute()) {
      markCartPageNeedsRefresh();
    }
  }


  function isCartPageRoute() {
    return window.location.pathname === "/cart";
  }

  function markDrawerShouldReopen() {
    sessionStorage.setItem("hf_reopen_cart_drawer", "1");
  }

  function shouldReopenDrawer() {
    return sessionStorage.getItem("hf_reopen_cart_drawer") === "1";
  }

  function clearDrawerReopenFlag() {
    sessionStorage.removeItem("hf_reopen_cart_drawer");
  }

  function markCartPageNeedsRefresh() {
    sessionStorage.setItem("hf_refresh_cart_page_on_drawer_close", "1");
  }

  function cartPageNeedsRefresh() {
    return sessionStorage.getItem("hf_refresh_cart_page_on_drawer_close") === "1";
  }

  function clearCartPageRefreshFlag() {
    sessionStorage.removeItem("hf_refresh_cart_page_on_drawer_close");
  }


  function bindCartUI(root) {
    if (!root) return;

    if (root.dataset.cartUiClickBound !== "true") {
      root.dataset.cartUiClickBound = "true";

      root.addEventListener("click", async (e) => {
        const minus = e.target.closest(selectors.qtyMinus);
        const plus = e.target.closest(selectors.qtyPlus);
        const remove = e.target.closest(selectors.remove);

        const context = root.matches(selectors.cartPage) ? "page" : "drawer";

        if (minus) {
          e.preventDefault();
          const key = minus.getAttribute("data-key");
          const input = root.querySelector(`${selectors.qtyInput}[data-key="${CSS.escape(key)}"]`);
          const qty = Math.max(0, (parseInt(input?.value || "1", 10) || 1) - 1);
          try {
            await cartChange(key, qty, context);
          } catch (error) {
            console.error(error);
          }
          return;
        }

        if (plus) {
          e.preventDefault();
          const key = plus.getAttribute("data-key");
          const input = root.querySelector(`${selectors.qtyInput}[data-key="${CSS.escape(key)}"]`);
          const qty = (parseInt(input?.value || "0", 10) || 0) + 1;
          try {
            await cartChange(key, qty, context);
          } catch (error) {
            console.error(error);
          }
          return;
        }

        if (remove) {
          e.preventDefault();
          const key = remove.getAttribute("data-key");
          try {
            await cartChange(key, 0, context);
          } catch (error) {
            console.error(error);
          }
        }

      });
    }

    if (root.dataset.cartUiChangeBound !== "true") {
      root.dataset.cartUiChangeBound = "true";

      root.addEventListener("change", async (e) => {
        const context = root.matches(selectors.cartPage) ? "page" : "drawer";
        const input = e.target.closest(selectors.qtyInput);
        if (input) {
          const key = input.getAttribute("data-key");
          const qty = Math.max(0, parseInt(input.value || "0", 10) || 0);
          try {
            await cartChange(key, qty, context);
          } catch (error) {
            console.error(error);
          }
          return;
        }

        const planSelect = e.target.closest(selectors.cartSellingPlanSelect);
        if (planSelect) {
          const lineIndex = Number(planSelect.getAttribute("data-line-index"));
          const variantId = Number(planSelect.getAttribute("data-variant-id"));
          const quantity = Number(planSelect.getAttribute("data-quantity") || 1);
          const sellingPlanId = planSelect.value ? Number(planSelect.value) : null;
          const previousValue = planSelect.dataset.previousValue || "";

          planSelect.dataset.previousValue = planSelect.value;

          try {
            planSelect.disabled = true;

            await changeLineSellingPlan({
              lineIndex,
              variantId,
              quantity,
              sellingPlanId,
              context,
            });

            planSelect.disabled = false;
          } catch (error) {
            console.error("Failed to change selling plan", error);
            planSelect.value = previousValue;
            planSelect.disabled = false;
          }
        }

      });
    }

    qsa(selectors.cartSellingPlanSelect, root).forEach((select) => {
      if (!select.dataset.previousValue) {
        select.dataset.previousValue = select.value || "";
      }
    });
  }

  function bindDrawerEvents() {
    qsa(selectors.openers).forEach((btn) => {
      if (btn.dataset.cartDrawerBound === "true") return;
      btn.dataset.cartDrawerBound = "true";

      btn.addEventListener("click", (e) => {
        e.preventDefault();
        openDrawer();
        refreshDrawer({ open: false });
      });
    });

    if (!document.documentElement.dataset.cartDrawerBackdropBound) {
      document.documentElement.dataset.cartDrawerBackdropBound = "true";

      document.addEventListener("click", (e) => {
        const bd = e.target.closest(selectors.backdrop);
        if (!bd) return;
        if (!state.isOpen) return;
        e.preventDefault();
        closeDrawer();
      });
    }

    const closeBtn = qs(selectors.closer);
    if (closeBtn && closeBtn.dataset.cartDrawerBound !== "true") {
      closeBtn.dataset.cartDrawerBound = "true";
      closeBtn.addEventListener("click", closeDrawer);
    }

    qsa(selectors.closeLink).forEach((a) => {
      if (a.dataset.cartDrawerBound === "true") return;
      a.dataset.cartDrawerBound = "true";
      a.addEventListener("click", () => closeDrawer());
    });

    if (!document.documentElement.dataset.cartDrawerEscapeBound) {
      document.documentElement.dataset.cartDrawerEscapeBound = "true";

      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && state.isOpen) closeDrawer();

        // Keep Tab cycling inside the open drawer.
        if (e.key === "Tab" && state.isOpen) {
          const drawer = qs(selectors.drawer);
          if (!drawer) return;
          const focusable = getFocusable(drawer);
          if (!focusable.length) {
            e.preventDefault();
            drawer.focus();
            return;
          }
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          const active = document.activeElement;
          if (e.shiftKey && (active === first || active === drawer || !drawer.contains(active))) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && (active === last || !drawer.contains(active))) {
            e.preventDefault();
            first.focus();
          }
        }
      });
    }

    const drawer = qs(selectors.drawer);
    if (!drawer) return;

    bindCartUI(drawer);
    bindDiscountEvents();
  }

  function bindAddToCartAjax() {
    qsa(selectors.addToCartForms).forEach((form) => {
      if (form.dataset.cartDrawerBound === "true") return;
      form.dataset.cartDrawerBound = "true";

      form.addEventListener("submit", async (e) => {
        e.preventDefault();

        try {
          await cartAdd(form);
        } catch (error) {
          console.error(error);
        }
      });
    });
  }

  function getDiscount() {
    return (sessionStorage.getItem("hf_discount_code") || "").trim();
  }

  function setDiscount(code) {
    sessionStorage.setItem("hf_discount_code", (code || "").trim());
  }

  function bindDiscountEvents() {
    const input = document.querySelector("[data-discount-input]");
    const applyBtn = document.querySelector("[data-discount-apply]");
    const checkoutLink = document.querySelector("[data-checkout-link]");

    if (!input || !applyBtn) return;

    const existing = getDiscount();
    if (existing) input.value = existing;

    if (applyBtn.dataset.cartDrawerBound !== "true") {
      applyBtn.dataset.cartDrawerBound = "true";

      applyBtn.addEventListener("click", () => {
        setDiscount(input.value);
        applyBtn.textContent = "Applied";
        setTimeout(() => (applyBtn.textContent = "Apply"), 1200);
      });
    }

    if (checkoutLink && checkoutLink.dataset.cartDrawerBound !== "true") {
      checkoutLink.dataset.cartDrawerBound = "true";

      checkoutLink.addEventListener("click", () => {
        const code = (input.value || "").trim();
        if (!code) return;

        const url = new URL(checkoutLink.href, window.location.origin);
        url.searchParams.set("discount", code);
        checkoutLink.href = url.toString();
      });
    }
  }

  // Scripts that add to the cart themselves (e.g. the landing page buy box,
  // which can add two lines at once) dispatch this to show the result.
  document.addEventListener("hf:cart:added", () => {
    if (!qs(selectors.drawer)) {
      window.location.href = "/cart";
      return;
    }
    openDrawer();
    refreshDrawer({ open: false });
    updateHeaderCount();
  });

  document.addEventListener("DOMContentLoaded", () => {
    bindDrawerEvents();
    bindAddToCartAjax();
    updateHeaderCount();

    const cartPage = qs(selectors.cartPage);
    if (cartPage) {
      bindCartUI(cartPage);
    }

    if (shouldReopenDrawer()) {
      clearDrawerReopenFlag();
      openDrawer();
      refreshDrawer({ open: false });
    }
  });
})();
